'use client'

import { DialogDismiss, Form } from '@ariakit/react'
import styled, { css, keyframes } from 'styled-components'

import {
  Button,
  Calendar,
  CheckMarkLine,
  Link,
  Switch,
  TypeStyle,
  TypeStyleBodyDefault,
  fromMd,
  timingDefault,
  zIndexPopovers,
} from '@syconium/little-miss-figgy'

import { NextDialog } from '../NextDialog.client'

export const PREVIEW_PANEL_GRAY = '#ccc'

export const rowCenter = css`
  display: flex;
  align-items: center;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${o => o.theme.spacing(5, 5, 0)};

  ${fromMd} {
    padding: ${o => o.theme.spacing(10, 10, 0)};
  }
`

export const Dialog = styled(NextDialog)`
  ${fromMd} {
    width: 560px;
  }
`

export const PreviewModeSection = styled.div`
  ${rowCenter};
  justify-content: space-between;
  gap: ${o => o.theme.spacing(2)};
  padding: ${o => o.theme.spacing(4)};
  background-color: ${o => o.theme.color.fill.subtle};
`

export const PreviewIcon = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  margin-right: ${o => o.theme.spacing(2)};
  border-radius: ${o => o.theme.borderRadius.full};
  background-color: ${o =>
    o.$active ? o.theme.color.stroke.celebratory.text.on.background : PREVIEW_PANEL_GRAY};
  transition: background-color ${timingDefault};
`

export const PreviewState = styled.span<{ $active: boolean }>`
  margin-left: ${o => o.theme.spacing(1)};
  color: ${o => (o.$active ? o.theme.color.stroke.celebratory.text.on.background : 'initial')};
  transition: color ${timingDefault};
`

export const PreviewRow = styled(TypeStyleBodyDefault)`
  ${rowCenter};
  font-weight: 700;
`

export const PreviewSwitch = styled(Switch)<{ $active: boolean }>`
  [role='presentation'] {
    background-color: ${o =>
      o.$active ? o.theme.color.stroke.celebratory.text.on.background : PREVIEW_PANEL_GRAY};
    transition: background-color ${timingDefault};
  }
`

export const DialogHeaderRow = styled.div`
  ${rowCenter};
  justify-content: space-between;
`

export const headerButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  position: relative;
  border-radius: ${o => o.theme.borderRadius.full};
  background-color: ${o => o.theme.color.fill.subtle};
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition: background-color ${timingDefault};

  &:disabled {
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    background-color: ${o => o.theme.color.fill.disabled.subtle};
  }
`

export const HeaderButton = styled.button`
  ${headerButtonStyles};
`

export const HeaderButtonRow = styled.div`
  display: flex;
  gap: ${o => o.theme.rowGap.tiny};
`

const tooltipFadeIn = keyframes`
  0% {
    transform: translateY(8px);
    opacity: 0;
  }
  20% {
    transform: translateX(0);
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

export const Tooltip = styled.div<{ $active: boolean }>`
  z-index: ${zIndexPopovers};
  border-radius: ${o => o.theme.borderRadius.sm};
  background: ${o => o.theme.color.fill.strong};
  color: ${o => o.theme.color.text.highContrast.on.darkImage};
  font-size: 11px;
  padding: 4px;
  top: -${o => o.theme.spacing(6)};
  left: -${o => o.theme.spacing(2)};
  display: ${o => (o.$active ? 'block' : 'none')};
  font-weight: 600;
  position: absolute;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  animation: ${tooltipFadeIn} 1.5s;
`

export const DialogClose = styled(DialogDismiss)`
  ${headerButtonStyles};
`

export const LinkIcon = styled(Link)<{ $active: boolean }>`
  transition: transform ${timingDefault};
  position: absolute;
  top: 9px;
  left: 9px;
  transform: translateY(${o => (o.$active ? '200%' : '0%')});
`

export const CheckMarkIcon = styled(CheckMarkLine)<{ $active: boolean }>`
  transition: transform ${timingDefault};
  position: absolute;
  transform: translateY(${o => (o.$active ? '0%' : '-200%')});

  path {
    fill: ${o => o.theme.color.stroke.celebratory.text.on.background};
  }
`

export const DialogHeader = styled.div`
  ${TypeStyle.css.headlineLarge};
  padding-bottom: ${o => o.theme.spacing(2)};
  border-bottom: 0;

  ${fromMd} {
    ${TypeStyle.css.headlineSmall};
  }
`

export const HotkeyHelper = styled.div`
  ${rowCenter};
  gap: ${o => o.theme.rowGap.tiny};
  padding-bottom: ${o => o.theme.spacing(5)};
  border-bottom: 1px solid ${o => o.theme.color.deprecated.stoneLight};
  color: ${o => o.theme.color.stroke.disabled.text.on.background};
  font-size: 11px;

  > div {
    ${rowCenter};
    min-width: 7px;
    height: 15px;
    padding: ${o => o.theme.spacing(0, 1)};
    border: 1px solid ${o => o.theme.color.deprecated.stoneLight};
    border-radius: ${o => o.theme.borderRadius.sm};
    font-size: 10px;
  }
`

export const Settings = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(5)};
  padding: ${o => o.theme.spacing(5, 0, 10)};
`

export const SettingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2)};
`

export const SettingLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 700;
  line-height: ${o => o.theme.spacing(2)};
  letter-spacing: 1.6px;
`

export const LocaleRow = styled(SettingRow)`
  flex-direction: row;
  gap: ${o => o.theme.rowGap.extraSmall};
  margin-top: 1px; // offset for box-shadow

  > div {
    flex: 1;
  }
`

export { DatePickerWrap } from './DatePickerWrap.styles'

export const DateIcon = styled(Calendar)<{ $disabled: boolean }>`
  position: absolute;
  right: ${o => o.theme.spacing(4)};
  top: ${o => o.theme.spacing(2)};
  padding: ${o => o.theme.spacing(1)};

  g {
    transition: stroke ${timingDefault};
    stroke: ${o =>
      o.$disabled ? o => o.theme.color.stroke.neutral.text.tertiary.on.background : 'currentColor'};
  }
`

export const SystemInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
  width: 100%;
  padding: ${o => o.theme.spacing(5, 5, 5)};
  background-color: ${o => o.theme.color.fill.subtle};
  font-size: 14px;
  transform: translateX(-${o => o.theme.spacing(6)});

  ${fromMd} {
    transform: translateX(-${o => o.theme.spacing(10)});
    padding: ${o => o.theme.spacing(5, 10, 10)};
  }
`

export const Divider = styled.div`
  width: 100%;
  margin: ${o => o.theme.spacing(1, 0)};
  border-top: 1px solid ${o => o.theme.color.deprecated.stoneLight};
`

export const SystemInfoRow = styled.div`
  ${rowCenter};
  justify-content: space-between;

  > div:nth-child(2) {
    font-weight: 600;
  }
`

export const Environment = styled.div`
  text-transform: capitalize;
`

export const SubmitButton = styled(Button)`
  transition: background-color ${timingDefault};
`
